import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, MessageCircle, Bug, RefreshCw, X } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface ConversationItem {
  phone: string;
  lastMessageAt: number;
  messageCount: number;
}

interface Message {
  id: number;
  phone: string;
  message: string;
  sender: 'user' | 'bot';
  createdAt: number;
}

const AdminConversations: React.FC = () => {
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchConversations();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/whatsapp/conversations');
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        setConversations(data.conversations || []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } catch (e) {
      console.error('Failed to fetch conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const openConversation = async (phone: string) => {
    try {
      const res = await apiFetch(`/api/public/admin/whatsapp/conversations/${phone}`);
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        setSelectedPhone(data.phone);
        setMessages(
          (data.messages || []).map((m: any) => ({
            ...m,
            sender: m.direction === 'outbound' || m.sender === 'bot' ? 'bot' : 'user',
          }))
        );
        setShowModal(true);
      }
    } catch (e) {
      console.error('Failed to open conversation:', e);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedPhone || !replyText.trim()) return;

    setSendLoading(true);
    setSendError(null);
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: selectedPhone, message: replyText.trim() })
      });

      if (res.ok) {
        setReplyText('');
      } else {
        let errMsg = '發送失敗，請稍後再試';
        try {
          const data = await res.json();
          errMsg = data.error || data.detail || errMsg;
        } catch { /* ignore */ }
        setSendError(errMsg);
      }
      // Always refresh so the outbound message (sent or failed) appears in the chat
      await openConversation(selectedPhone);
    } catch (e) {
      console.error('Failed to send message:', e);
      setSendError('發送失敗：無法連線到伺服器');
      await openConversation(selectedPhone);
    } finally {
      setSendLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const fetchWebhookLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/webhook-logs?limit=50');
      if (res.ok) {
        setLogs(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch webhook logs:', e);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const filteredConversations = conversations
    .filter(conv => conv.phone.includes(searchTerm))
    .sort((a, b) => b.lastMessageAt - a.lastMessageAt);

  return (
    <AdminLayout currentPage="conversations" onLogout={handleLogout}>
      <div className="mb-6"></div>

      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-red-600" />
          WhatsApp 對話
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchConversations}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> 重新整理
          </button>
          <button
            onClick={() => { setShowLogs(true); fetchWebhookLogs(); }}
            className="flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <Bug size={16} /> Webhook 日誌
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="搜尋客戶電話號碼..."
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none dark:bg-gray-800 dark:text-white"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Conversations List */}
      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
          加載中...
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
          {conversations.length === 0 ? '沒有對話記錄' : '沒有符合條件的對話'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredConversations.map(conv => (
            <div
              key={conv.phone}
              onClick={() => openConversation(conv.phone)}
              className="bg-white dark:bg-gray-800 rounded shadow p-4 cursor-pointer hover:shadow-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-all border-l-4 border-red-500"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-semibold text-lg">📱 {conv.phone}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    最後消息：{conv.lastMessageAt ? new Date(conv.lastMessageAt * 1000).toLocaleString('zh-HK') : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    消息數量：{conv.messageCount || 0} 條
                  </div>
                </div>
                <div className="text-red-600 font-semibold whitespace-nowrap ml-4">查看對話 →</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Webhook Logs Modal */}
      {showLogs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="sticky top-0 bg-gradient-to-r from-red-500 to-red-600 p-4 flex justify-between items-center border-b text-white rounded-t-lg">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5" />
                <h2 className="text-xl font-bold">Webhook 日誌（最近 50 筆）</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchWebhookLogs}
                  className="p-1.5 hover:bg-white/20 rounded"
                  title="重新整理"
                >
                  <RefreshCw size={18} />
                </button>
                <button onClick={() => setShowLogs(false)} className="text-2xl hover:text-gray-200">✕</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              {logsLoading ? (
                <p className="text-center text-gray-500 py-8">載入中...</p>
              ) : logs.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">暫無 Webhook 記錄</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 p-3 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-800 dark:text-white">來源：{log.source}</span>
                        <span className="text-xs text-gray-500">
                          {log.createdAt ? new Date(log.createdAt * 1000).toLocaleString('zh-HK') : 'N/A'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                        電話：<span className="font-mono">{log.extractedPhone || '-'}</span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                        訊息：<span className="font-mono">{log.extractedText || '-'}</span>
                      </div>
                      <details className="text-xs">
                        <summary className="cursor-pointer text-red-600 hover:text-red-800">查看原始 Payload</summary>
                        <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded overflow-x-auto text-gray-700 dark:text-gray-300">
                          {JSON.stringify(JSON.parse(log.payload || '{}'), null, 2)}
                        </pre>
                      </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Conversation Modal */}
      {showModal && selectedPhone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-red-500 to-red-600 p-4 flex justify-between items-center border-b text-white rounded-t-lg">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <div>
                  <h2 className="text-xl font-bold">對話詳情</h2>
                  <p className="text-sm text-red-100">📱 {selectedPhone}</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-2xl hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
              {messages.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">沒有消息</p>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'bot' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.sender === 'bot'
                          ? 'bg-red-600 text-white rounded-br-none'
                          : 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-white rounded-bl-none'
                      }`}
                    >
                      <div className="text-sm mb-1 break-words">{msg.message}</div>
                      <div
                        className={`text-xs ${
                          msg.sender === 'bot' ? 'text-red-200' : 'text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {msg.createdAt ? new Date(msg.createdAt * 1000).toLocaleString('zh-HK') : 'N/A'}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg space-y-2">
              {sendError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                  <span className="font-semibold">發送失敗：</span>{sendError}
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={replyText}
                  onChange={e => { setReplyText(e.target.value); setSendError(null); }}
                  onKeyDown={handleKeyDown}
                  placeholder="輸入回覆訊息... (按 Enter 發送)"
                  rows={2}
                  className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none dark:bg-gray-800 dark:text-white"
                  disabled={sendLoading}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={sendLoading || !replyText.trim()}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {sendLoading ? '發送中...' : '發送'}
                </button>
              </div>
              <div className="flex justify-end">
                <a
                  href={`https://api.whatsapp.com/send?phone=${selectedPhone}&text=${encodeURIComponent(replyText.trim())}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-red-600 hover:underline"
                >
                  如 API 發送失敗，可點此用手機 WhatsApp 開啟對話
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminConversations;
